import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template9 = () => {
    const navigate = useNavigate();
    return (
        <div className="bg-white min-h-screen font-sans">
            <header className="bg-white shadow-sm p-4 relative z-10">
                <div className="container mx-auto flex justify-between items-center">
                    <button
                        onClick={() => navigate('/TemplateThemes')}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                        <ArrowLeft size={18} /> Back to Templates
                    </button>
                    <h1 className="text-4xl font-light text-center tracking-widest text-gray-900">ELEGANT</h1>
                    <div className="w-24"></div> {/* Placeholder for alignment */}
                </div>
            </header>
            <main>
                {/* Hero Slider Section - Static Replica */}
                <section className="h-screen w-full relative text-white">
                    <div className="w-full h-full">
                        <img 
                            src="https://placehold.co/1920x1080/4A4A4A/FFFFFF?text=Elegant+Hero+Slider"
                            alt="Hero Banner" 
                            className="absolute z-0 w-full h-full object-cover" 
                        />
                        <div className="absolute z-10 inset-0 bg-black/50"></div>
                        <div className="relative z-20 flex flex-col items-center justify-center h-full text-center p-8">
                            <h1 className="text-5xl md:text-7xl font-extrabold drop-shadow-lg">Elegant Look</h1>
                            <p className="mt-4 text-lg max-w-xl mx-auto drop-shadow-md">Timeless designs for the modern individual.</p>
                            <button className="mt-8 bg-white text-black font-semibold px-6 py-3 rounded-full shadow-lg hover:bg-gray-200 transition-colors">
                                Shop Now
                            </button>
                        </div>
                    </div>
                </section>

                {/* Category Grid Section - Static Replica */}
                <section className="bg-white py-20">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Shop By Category</h2>
                            <p className="text-gray-500 mt-2">Find your perfect look from our curated collections.</p>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                            {[
                                { name: "Indianwear", img: "https://placehold.co/400x500/A0A0A0/FFFFFF?text=Indianwear" },
                                { name: "Westernwear", img: "https://placehold.co/400x500/B0B0B0/FFFFFF?text=Westernwear" },
                                { name: "Blazers", img: "https://placehold.co/400x500/C0C0C0/FFFFFF?text=Blazers" },
                                { name: "Kurtis", img: "https://placehold.co/400x500/D0D0D0/FFFFFF?text=Kurtis" }
                            ].map((cat, index) => (
                                <div key={index} className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
                                    <img src={cat.img} alt={cat.name} className="w-full h-full object-cover aspect-[4/5] transition-transform duration-500 group-hover:scale-105" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                                    <div className="absolute bottom-0 w-full p-4 text-white">
                                        <h3 className="font-bold text-lg">{cat.name}</h3>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
                
                {/* Lookbook Section - Static Replica */}
                <section className="bg-gray-50 py-20">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">The Lookbook</h2>
                            <p className="text-gray-500 mt-2">Explore our hand-picked selections and visual stories.</p>
                        </div>
                        <div className="flex overflow-x-auto snap-x snap-mandatory space-x-6 pb-4">
                            {[
                                { title: "New Arrivals", subtitle: "Fresh styles for a fresh season.", img: "https://placehold.co/600x400/E0D0C0/555555?text=Lookbook+1" },
                                { title: "Holiday Special", subtitle: "Shop our festive collection.", img: "https://placehold.co/600x400/C0C0C0/555555?text=Lookbook+2" },
                                { title: "Summer Style", subtitle: "Light, airy, and chic.", img: "https://placehold.co/600x400/B0B0B0/555555?text=Lookbook+3" },
                                { title: "Winter Trends", subtitle: "Stay warm in style.", img: "https://placehold.co/600x400/A0A0A0/555555?text=Lookbook+4" }
                            ].map((banner, index) => (
                                <div key={index} className="flex-none w-96 snap-start">
                                    <div className="relative overflow-hidden rounded-lg shadow-lg">
                                        <img src={banner.img} alt={banner.title} className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-black/40"></div>
                                        <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center p-6">
                                            <h3 className="text-2xl font-bold">{banner.title}</h3>
                                            <p className="mt-2 max-w-xs text-sm">{banner.subtitle}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Product Grid Section - Static Replica */}
                <section className="bg-white" id="product-grid">
                    <div className="max-w-7xl mx-auto px-4 py-24">
                        <div className="text-center">
                            <h2 className="text-4xl font-bold text-stone-800 mb-12">Discover Our Products</h2>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                            {[1, 2, 3, 4, 5, 6, 7, 8].map(id => (
                                <div key={id} className="bg-gray-50 p-4 rounded-lg shadow-sm">
                                    <img src={`https://placehold.co/400x500/E0E0E0/333333?text=Product+${id}`} alt={`Product ${id}`} className="w-full h-64 object-cover rounded-md mb-4" />
                                    <h3 className="font-semibold text-gray-800 text-lg">Product Title {id}</h3>
                                    <p className="text-gray-600 mt-1">$49.99</p>
                                    <button className="mt-3 w-full px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors">Add to Cart</button>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </main>
            <footer className="bg-gray-900 text-white text-center p-6 mt-8">
                <p>&copy; 2025 Elegant Store. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default Template9;